'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EncountersInfoTooltip } from 'src/components/EncountersInfoTooltip';
import { ProviderResultItem } from 'src/components/Maps/ResultsList/ProviderResultItem';
import { isReadyCohortVolume, resolveClaimsMetric } from 'src/utils/encounterLabels';
import { useOpenProviderProfile } from 'src/hooks/drawers/useOpenProviderProfile';
import {
  selectDiscoveryMapActiveMarker,
  setActiveMarker as setReduxActiveMarker,
} from 'src/redux/slices/discoveryMapSlice';
import { useResultsList } from '../../context';
import useDiscoveryList from 'src/hooks/discovery/useDiscoveryList';
import { USER_SETTINGS } from 'src/hooks/users/constants';
import useUserSettings from 'src/hooks/users/useUserSettings';
import { useSavedVolume } from 'src/hooks/savedVolume/useSavedVolume';
import { LOCATION } from 'src/hooks/filters/constants';
import { useAppDispatch, useAppSelector, resetAndSyncCodeSet } from 'src/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface Provider {
  provider_id: string;
  id: string | number;
  name: string;
  specialty: string;
  soc_type?: string;
  soc_subtype?: string;
  primary_address: {
    city: string;
    state: string;
  };
  volume?: string;
  claims?: number;
  patients: number;
  encounters?: number;
  content_type?: 'center' | 'hcp' | 'clinic' | 'HCP' | 'C';
  [key: string]: any;
}

interface ResultsItemProps {
  provider: Provider;
}

// Use React.memo to prevent unnecessary re-renders when props haven't changed
const ResultsItem: React.FC<ResultsItemProps> = ({ provider }) => {
  const { state, dispatch } = useResultsList();
  const { sort } = useDiscoveryList();
  const { settings, setSetting } = useUserSettings();
  const { appliedSavedVolume: discoveryCodeSet } = useSavedVolume(
    LOCATION.DISCOVERY
  );
  const { setSavedVolume: setOutboundCodeSet } = useSavedVolume(
    LOCATION.OUTBOUND_REFERRALS
  );
  const reduxDispatch = useAppDispatch();
  const activeMarker = useAppSelector(selectDiscoveryMapActiveMarker);
  const rowRef = useRef<HTMLDivElement>(null);
  const [isRowHovered, setIsRowHovered] = useState(false);

  // The row's pin is the active one on the map (hovered or clicked there).
  const isMapActive =
    activeMarker != null &&
    [provider.provider_id, provider.id].some(
      (id) => id != null && String(id) === String(activeMarker)
    );

  // Bring the row into view when its pin is activated on the map. A hover on the row
  // itself also sets the active marker; don't scroll the list under the cursor then.
  useEffect(() => {
    if (isMapActive && !isRowHovered) {
      rowRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [isMapActive, isRowHovered]);
  const { showEncountersTotals } = useFlags();

  // For encounters (cohort) searches, source the claims value from the encounters
  // metric and label it "Encounters" to match. Gated by the flag, and on the cohort
  // being READY — a non-READY cohort has no encounters value to show or name.
  // resolveClaimsMetric derives value and label together, so a provider with no
  // `encounters` field falls back to claims *and* keeps the "Claims" label.
  const {
    value: claimsValue,
    label: claimsLabel,
    usedEncounters: useEncounters,
  } = resolveClaimsMetric(
    provider,
    isReadyCohortVolume(discoveryCodeSet),
    !!showEncountersTotals
  );

  const isSelected = state.selectedProviders.some(
    (p) => p.provider_id === provider.provider_id
  );

  const handleCheckboxChange = useCallback(() => {
    if (isSelected) {
      dispatch({
        type: 'SET_SELECTED_PROVIDERS',
        payload: state.selectedProviders.filter(
          (p) => p.provider_id !== provider.provider_id
        ),
      });
    } else {
      dispatch({
        type: 'SET_SELECTED_PROVIDERS',
        payload: [...state.selectedProviders, provider],
      });
    }
  }, [provider, isSelected, dispatch, state.selectedProviders]);

  const handleProviderClick = useCallback(() => {
    if (settings?.prospectMode === 'sending') {
      setSetting(USER_SETTINGS.PROSPECT_RELATIONSHIP, 'outbound');

      // Reset and sync code set from discovery to OutboundReferralTable
      if (discoveryCodeSet) {
        reduxDispatch(
          resetAndSyncCodeSet({
            tableType: LOCATION.OUTBOUND_REFERRALS,
            newCodeSetId: discoveryCodeSet.id,
            defaultMode: 'sending',
            volumeType: discoveryCodeSet.volumeType,
          })
        );

        // Also update saved volume for proper integration
        setOutboundCodeSet(discoveryCodeSet);
      }
    } else {
      setSetting(USER_SETTINGS.PROSPECT_RELATIONSHIP, 'inbound');
    }
  }, [
    setSetting,
    settings?.prospectMode,
    discoveryCodeSet,
    setOutboundCodeSet,
    reduxDispatch,
  ]);

  const openProviderProfile = useOpenProviderProfile();
  const handleNameClick = useCallback(() => {
    handleProviderClick();
    openProviderProfile(provider.provider_id, provider.content_type || provider.type);
  }, [handleProviderClick, openProviderProfile, provider]);

  return (
    <ProviderResultItem
      ref={rowRef}
      data-testid="results-item"
      isActive={isMapActive}
      provider={provider}
      city={provider?.primary_address?.city}
      state={provider?.primary_address?.state}
      isSelected={isSelected}
      onToggleSelect={handleCheckboxChange}
      onNameClick={handleNameClick}
      stats={[
        {
          key: 'claims',
          label: (
            <>
              {claimsLabel}
              {useEncounters && <EncountersInfoTooltip />}
            </>
          ),
          labelTestId: 'results-item-volume-label',
          value: claimsValue,
          isActive: sort === 'claims',
        },
        {
          key: 'patients',
          label: 'Patients',
          value: provider?.patients,
          isActive: sort === 'patients',
        },
      ]}
      lists={provider.provider_lists}
      onMouseEnter={() => {
        setIsRowHovered(true);
        reduxDispatch(setReduxActiveMarker(provider.id));
      }}
      onMouseLeave={() => {
        setIsRowHovered(false);
        reduxDispatch(setReduxActiveMarker(null));
      }}
    />
  );
};

export default ResultsItem;
